import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, Check, X, Cpu, MessageSquare, Image, Video, Music, Bot } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import client from '../api/client'

interface AiModel {
  id: number
  model_name: string
  display_name: string | null
  provider: string | null
  category: string
  is_paid: boolean
  icon_url: string | null
  is_active: boolean
  cost_per_million_usd: number | null
}

const CATEGORIES: Record<string, { label: string; icon: React.ReactNode }> = {
  chat: { label: 'Чат-модели', icon: <MessageSquare className="w-4 h-4" /> },
  image: { label: 'Изображения', icon: <Image className="w-4 h-4" /> },
  video: { label: 'Видео', icon: <Video className="w-4 h-4" /> },
  music: { label: 'Музыка', icon: <Music className="w-4 h-4" /> },
}

const CATEGORY_KEYS = Object.keys(CATEGORIES)



export default function AiModelsPage() {
  const { user } = useAuthStore()
  const [models, setModels] = useState<AiModel[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('chat')
  const [priceMap, setPriceMap] = useState<Record<string, any>>({})

  useEffect(() => {
    Promise.all([
      client.get('/models/ai'),
      client.get('/admin/ai-models/calculated-prices')
    ])
      .then(([modelsRes, pricesRes]) => {
        const active = modelsRes.data.filter((m: AiModel) => m.is_active === true)
        setModels(active)
        setPriceMap(pricesRes.data || {})
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  // Фильтруем модели только по табам (4 категории: chat, image, video, music)
  const filteredModels = models.filter(m => m.category === activeTab)

  // Helper: получить иконку провайдера
  const providerIcon = (name: string | null) => {
    if (!name) return null
    const n = name.toLowerCase()
    if (n.includes('typegpt') || n === 'typegpt') return '🧠'
    if (n.includes('openai') || n.includes('gpt')) return '🤖'
    if (n.includes('claude') || n.includes('anthropic')) return '🟤'
    if (n.includes('gemini') || n.includes('google')) return '🔵'
    if (n.includes('grok') || n.includes('xai')) return '⚫'
    if (n.includes('deepseek')) return '🟢'
    if (n.includes('together')) return '🟣'
    if (n.includes('music') || n.includes('audio')) return '🎵'
    if (n.includes('flux') || n.includes('stable') || n.includes('sdxl')) return '🎨'
    return '🤖'
  }

  const modelCost = (m: AiModel) => {
    // Try to get price from priceMap (token/star values used in dropdowns)
    const p = priceMap[m.model_name]
    if (p?.input !== undefined && p?.unit) {
      const inp = p.input
      const out = p.output || 0
      const unit = (p.unit && p.unit.startsWith('stars')) ? '⭐' : 'токенов'
      if (inp === 0 && out === 0) return 'Бесплатно'
      if (out > 0) return `${inp}/${out} ${unit}`
      return `${inp} ${unit}`
    }
    // Fallback to old cost_per_million_usd
    if (m.cost_per_million_usd === null || m.cost_per_million_usd === undefined) return null
    const perReq = m.cost_per_million_usd / 1_000_000
    const per1000Reqs = perReq * 1000
    if (per1000Reqs < 0.01) return `${(perReq * 1_000_000).toFixed(2)} ₽/1M токенов`
    return `${perReq.toFixed(4)} ₽/запрос (примерно)`
  }

  const totalCount = models.length
  const activeCount = models.filter(m => m.is_active).length
  const freeCount = models.filter(m => !m.is_paid).length
  const tabCount = (key: string) => models.filter(m => m.category === key).length

  return (
    <div>

      <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold gradient-text">AI-модели Chat-Neuro.ru</h1>
              <p className="text-xs text-gray-500">
                Все доступные модели нейросетей — актуальный список
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {!loading && models.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6 mb-4 text-xs text-gray-400">
            <span className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/5">
              Всего работает моделей: <strong className="text-white">{totalCount}</strong>
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20 text-green-300">
              Платных: <strong>{activeCount - freeCount}</strong>
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300">
              Бесплатных: <strong>{freeCount}</strong>
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto">
          {CATEGORY_KEYS.map(key => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === key
                  ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/5 border border-transparent'
              }`}
            >
              <span className={activeTab === key ? 'text-indigo-300' : 'text-gray-500'}>{CATEGORIES[key].icon}</span>
              {CATEGORIES[key].label}
              <span className={`text-[10px] ${activeTab === key ? 'text-indigo-400' : 'text-gray-600'}`}>
                {tabCount(key)}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="text-center py-16">
            <Bot className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">В этой категории пока нет моделей</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {filteredModels.map(m => (
              <div
                key={m.id}
                className={`glass-card p-4 flex items-center gap-3 transition-all ${
                  !m.is_active ? 'opacity-50' : ''
                }`}
              >
                {/* Status dot */}
                <div className={`w-2 h-2 rounded-full shrink-0 ${m.is_active ? 'bg-green-400' : 'bg-red-400'}`} />

                {/* Icon */}
                {m.icon_url ? (
                  <img src={m.icon_url} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-sm shrink-0">
                    {providerIcon(m.provider || m.model_name)}
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-medium text-white truncate">
                      {m.display_name || m.model_name}
                    </span>
                    {!m.is_paid && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 font-medium leading-none">FREE</span>
                    )}
                    {m.is_paid && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 font-medium leading-none">PAID</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-gray-500 truncate">{m.model_name}</span>
                    {m.provider && (
                      <span className="text-[10px] text-gray-500 truncate">{m.provider}</span>
                    )}
                  </div>
                  {m.cost_per_million_usd !== null && m.cost_per_million_usd !== undefined && (
                    <p className="text-[9px] text-gray-600 mt-0.5">{modelCost(m)}</p>
                  )}
                </div>

                {/* Active badge */}
                <div className="shrink-0">
                  {m.is_active ? (
                    <Check className="w-4 h-4 text-green-400/60" />
                  ) : (
                    <X className="w-4 h-4 text-red-400/40" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="mt-8 glass-card p-6 text-center">
          <p className="text-xs text-gray-500">
            Список моделей обновляется автоматически. Статус модели (вкл/выкл) показывает,
            доступна ли она в данный момент. Бесплатные модели доступны всем пользователям.
          </p>
          <p className="text-[10px] text-gray-600 mt-2">
            📢 Не нашли модель?{' '}
            <Link to="/support" className="text-indigo-400 hover:text-indigo-300 whitespace-nowrap">
              Напишите в поддержку
            </Link>
          </p>
        </div>
      </div>


    </div>
  )
}
